import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddTeamMutation as AddTeam } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddTeamMutation from './AddTeamMutation';
import ContactPicker, { Participant } from '../Contacts/ContactPicker';
import EventPicker from '../../Events/EventPicker';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

const schema = yup.object({
  title: yup.string().required(),
});

export interface AddTeamFormProps {
  eventId?: string;
  onSuccess?: (team: AddTeam['addTeam']) => void;
}

const AddTeamForm = ({ eventId: preselectEventId, onSuccess }: AddTeamFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: '',
    },
  });

  const [eventId, setEventId] = useState<string | null>(preselectEventId || null);
  const [participant, setParticipant] = useState<Participant>(null);

  const [addTeam, { error, loading }] = useMutation(AddTeamMutation, {
    variables: {
      input: {
        event: { id: eventId },
        captain: { id: participant?.id },
        title: form.values.title,
      },
    },
    onCompleted: ({ addTeam }) => {
      notifier.success(t('notifications.team_added'));
      onSuccess?.(addTeam);
    },
  });

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addTeam()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_team')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_team_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} attributes={{ title: t('team_name') }} />

        <UI.HR />

        <UI.InputGroup style={{ zIndex: 20 }}>
          <UI.InputLabel>
            {t('event')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <EventPicker
            eventId={eventId}
            onChange={(event) => setEventId(event?.id || null)}
            nullable={false}
          />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="title">
            {t('team_name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={50}
            id="title"
            autoFocus={!!eventId}
          />
          <UI.ErrorMessages attribute={t('team_name')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.participant_id, touched: form.touched.participant_id }}>
          <UI.InputLabel>
            {t('team_captain')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <ContactPicker
            participant={participant}
            onChange={(participant) => setParticipant(participant)}
            nullable={false}
            disabled={!eventId}
          />
          <UI.ErrorMessages attribute={t('event_title')} errors={errors.participant_id} />
        </UI.InputGroup>
      </UI.FormGrid>

      <UI.HR />

      <UI.FormButtons
        form={form}
        submitLabel={t('add_team')}
        loading={loading}
        valid={valid}
      />
    </UI.Form>
  );
};

export default AddTeamForm;
